import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Button, Card, Descriptions, Input, Modal, Radio, Space, Spin, Table, Tag, Typography, message } from 'antd'
import { ArrowLeftOutlined, StopOutlined } from '@ant-design/icons'
import { cancelSubscription, getSubscription, getSubscriptionInvoices } from '../../services/subscriptions'
import { formatTimestamp } from '../../utils/format'
import { formatAmount } from '../../utils/orderDisplay'
import type {
  MerchantSubscriptionDetail,
  MerchantSubscriptionInvoiceSummary,
  Pagination,
  SubscriptionCancelType
} from '../../types'
import {
  billingReasonText,
  canCancelSubscription,
  cancellationStatusMeta,
  cancelTypeText,
  formatCard,
  formatSubscriptionTerm,
  invoiceStatusMeta,
  orderStatusMeta,
  subscriptionStatusMeta
} from './subscriptionLabels'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input
const initialPagination: Pagination = { page: 1, size: 20, total: 0, totalPages: 0 }

export default function SubscriptionDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [subscription, setSubscription] = useState<MerchantSubscriptionDetail | null>(null)
  const [invoices, setInvoices] = useState<MerchantSubscriptionInvoiceSummary[]>([])
  const [invoicePagination, setInvoicePagination] = useState<Pagination>(initialPagination)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [invoiceLoadError, setInvoiceLoadError] = useState<string | null>(null)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelType, setCancelType] = useState<SubscriptionCancelType>('PERIOD_END')
  const [cancelReason, setCancelReason] = useState('')
  const [submittingCancel, setSubmittingCancel] = useState(false)

  const loadDetail = async () => {
    if (!id) return
    setLoading(true)
    try {
      const res = await getSubscription(id)
      setSubscription(res.data)
      setLoadError(null)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '載入訂閱詳情失敗'
      message.error(errorMessage)
      setLoadError(errorMessage)
      setSubscription(null)
    } finally {
      setLoading(false)
    }
  }

  const loadInvoices = async (page: number = 1, size: number = initialPagination.size) => {
    if (!id) return
    setInvoiceLoading(true)
    try {
      const res = await getSubscriptionInvoices(id, { page, size })
      setInvoices(res.data.items)
      setInvoicePagination(res.data.pagination)
      setInvoiceLoadError(null)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '載入賬單記錄失敗'
      message.error(errorMessage)
      setInvoiceLoadError(errorMessage)
      setInvoices([])
      setInvoicePagination(initialPagination)
    } finally {
      setInvoiceLoading(false)
    }
  }

  useEffect(() => {
    loadDetail()
    loadInvoices(1)
  }, [id])

  const openCancelModal = (nextCancelType: SubscriptionCancelType) => {
    setCancelType(nextCancelType)
    setCancelReason('')
    setCancelModalOpen(true)
  }

  const submitCancel = async () => {
    if (!id) return
    setSubmittingCancel(true)
    try {
      await cancelSubscription(id, {
        cancelAtPeriodEnd: cancelType === 'PERIOD_END',
        reason: cancelReason.trim() || undefined,
        requestKey: `${id}:${cancelType}:${Date.now()}`
      })
      message.success(cancelType === 'PERIOD_END' ? '已提交週期末取消' : '已提交立即取消')
      setCancelModalOpen(false)
      await Promise.all([loadDetail(), loadInvoices(invoicePagination.page, invoicePagination.size)])
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '取消訂閱失敗')
    } finally {
      setSubmittingCancel(false)
    }
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
  }

  if (!subscription) {
    return (
      <Alert
        type="error"
        showIcon
        message={loadError ? '載入訂閱詳情失敗' : '訂閱不存在或無權訪問'}
        description={loadError || undefined}
        action={loadError ? <Button size="small" onClick={loadDetail}>重新載入</Button> : undefined}
      />
    )
  }

  const statusMeta = subscriptionStatusMeta[subscription.status]
  const latestCancellation = subscription.latestCancellationRequest
  const canCancel = canCancelSubscription(subscription.status)

  const invoiceColumns = [
    { title: '賬單 ID', dataIndex: 'id', key: 'id', width: 220 },
    {
      title: '期數',
      dataIndex: 'cycleNo',
      key: 'cycleNo',
      width: 80
    },
    {
      title: '類型',
      dataIndex: 'billingReason',
      key: 'billingReason',
      width: 130,
      render: (value: MerchantSubscriptionInvoiceSummary['billingReason']) => billingReasonText[value]
    },
    {
      title: '金額',
      key: 'amount',
      width: 130,
      render: (_: unknown, record: MerchantSubscriptionInvoiceSummary) => `${formatAmount(record.amount)} ${record.currency}`
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: MerchantSubscriptionInvoiceSummary['status']) => {
        const meta = invoiceStatusMeta[value]
        return <Tag color={meta.color}>{meta.label}</Tag>
      }
    },
    {
      title: '週期',
      key: 'period',
      width: 260,
      render: (_: unknown, record: MerchantSubscriptionInvoiceSummary) => (
        <span>{formatTimestamp(record.periodStartAt)} ～ {formatTimestamp(record.periodEndAt)}</span>
      )
    },
    {
      title: '支付時間',
      dataIndex: 'paidAt',
      key: 'paidAt',
      width: 180,
      render: (value?: number | null) => formatTimestamp(value)
    },
    {
      title: '關聯訂單',
      key: 'order',
      width: 220,
      render: (_: unknown, record: MerchantSubscriptionInvoiceSummary) => {
        if (!record.order) return '-'
        const meta = orderStatusMeta[record.order.status]
        return (
          <Space direction="vertical" size={0}>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/dashboard/orders/${record.order?.id}`)}>
              {record.order.id}
            </Button>
            <span>{formatAmount(record.order.amount)} {record.order.currency} <Tag color={meta.color}>{meta.label}</Tag></span>
          </Space>
        )
      }
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <Space size={24} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard/subscriptions')}>
            返回訂閱列表
          </Button>
          <Title level={4} style={{ margin: 0 }}>訂閱詳情</Title>
        </Space>
        <Space wrap>
          <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
          {canCancel && (
            <>
              <Button icon={<StopOutlined />} onClick={() => openCancelModal('PERIOD_END')}>
                週期末取消
              </Button>
              <Button danger icon={<StopOutlined />} onClick={() => openCancelModal('IMMEDIATE')}>
                立即取消
              </Button>
            </>
          )}
        </Space>
      </div>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card title="基本信息">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="訂閱 ID">{subscription.id}</Descriptions.Item>
            <Descriptions.Item label="狀態"><Tag color={statusMeta.color}>{statusMeta.label}</Tag></Descriptions.Item>
            <Descriptions.Item label="產品">{subscription.productName || subscription.productId}</Descriptions.Item>
            <Descriptions.Item label="計劃">{subscription.plan.name || subscription.plan.id}</Descriptions.Item>
            <Descriptions.Item label="客戶郵箱">{subscription.customerEmail || '-'}</Descriptions.Item>
            <Descriptions.Item label="客戶名稱">{subscription.customerName || '-'}</Descriptions.Item>
            <Descriptions.Item label="創建時間">{formatTimestamp(subscription.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="更新時間">{formatTimestamp(subscription.updatedAt)}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="計費與支付方式">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="計劃金額">{formatAmount(subscription.plan.amount)} {subscription.plan.currency}</Descriptions.Item>
            <Descriptions.Item label="計費週期">{formatSubscriptionTerm(subscription.plan.billingInterval, subscription.plan.billingIntervalCount, subscription.plan.totalBillingCycles)}</Descriptions.Item>
            <Descriptions.Item label="當前週期開始">{formatTimestamp(subscription.currentPeriodStartAt)}</Descriptions.Item>
            <Descriptions.Item label="當前週期結束">{formatTimestamp(subscription.currentPeriodEndAt)}</Descriptions.Item>
            <Descriptions.Item label="下次扣款">{formatTimestamp(subscription.nextBillingAt)}</Descriptions.Item>
            <Descriptions.Item label="支付方式">信用卡 {formatCard(subscription.card?.brand, subscription.card?.last4)}</Descriptions.Item>
            <Descriptions.Item label="卡有效期">{subscription.card?.expMonth && subscription.card?.expYear ? `${subscription.card.expMonth}/${subscription.card.expYear}` : '-'}</Descriptions.Item>
            <Descriptions.Item label="最近扣款">
              {subscription.latestInvoice
                ? `${formatAmount(subscription.latestInvoice.amount)} ${subscription.latestInvoice.currency} · ${invoiceStatusMeta[subscription.latestInvoice.status].label}`
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="取消狀態">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="週期末取消">{subscription.cancelAtPeriodEnd ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="計劃取消時間">{formatTimestamp(subscription.cancelAt)}</Descriptions.Item>
            <Descriptions.Item label="實際取消時間">{formatTimestamp(subscription.cancelledAt)}</Descriptions.Item>
            <Descriptions.Item label="最近取消請求">
              {latestCancellation ? (
                <Space direction="vertical" size={0}>
                  <span>{cancelTypeText[latestCancellation.cancelType]} · <Tag color={cancellationStatusMeta[latestCancellation.status].color}>{cancellationStatusMeta[latestCancellation.status].label}</Tag></span>
                  <Text type="secondary">{formatTimestamp(latestCancellation.requestedAt)}</Text>
                </Space>
              ) : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="歷史賬單 / 訂單記錄">
          {invoiceLoadError && (
            <Alert
              type="error"
              showIcon
              message="載入賬單記錄失敗"
              description={invoiceLoadError}
              action={<Button size="small" onClick={() => loadInvoices(invoicePagination.page || 1, invoicePagination.size || initialPagination.size)}>重新載入</Button>}
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            columns={invoiceColumns}
            dataSource={invoices}
            rowKey="id"
            loading={invoiceLoading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: invoicePagination.page,
              pageSize: invoicePagination.size,
              total: invoicePagination.total,
              onChange: (page, size) => loadInvoices(page, size)
            }}
          />
        </Card>
      </Space>

      <Modal
        title={cancelType === 'PERIOD_END' ? '週期末取消訂閱' : '立即取消訂閱'}
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        onOk={submitCancel}
        okText={cancelType === 'PERIOD_END' ? '確認週期末取消' : '確認立即取消'}
        okButtonProps={{ danger: cancelType === 'IMMEDIATE' }}
        confirmLoading={submittingCancel}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {cancelType === 'PERIOD_END' ? (
            <Alert
              type="info"
              showIcon
              message="當前週期結束前訂閱仍有效，到期後不再續費。"
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              message="立即取消只阻止後續續費，不會自動退款。"
              description="如需退還已支付週期費用，請到對應訂單發起退款流程。"
            />
          )}
          <Radio.Group value={cancelType} onChange={(event) => setCancelType(event.target.value)}>
            <Radio value="PERIOD_END">週期末取消</Radio>
            <Radio value="IMMEDIATE">立即取消</Radio>
          </Radio.Group>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            本操作只會使用 Kyren 訂閱 ID，不會向商戶展示或要求輸入上游通道信息。
          </Paragraph>
          <TextArea
            rows={4}
            maxLength={500}
            showCount
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder="取消原因（可選，僅供商戶與運營排查）"
          />
        </Space>
      </Modal>
    </div>
  )
}
